import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { positions, countries } from "../mock/mock";
import api from "../lib/api";
import PlayerCard from "../components/PlayerCard";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Search as SearchIcon, SlidersHorizontal, X, Lock } from "lucide-react";

const Search = () => {
  const { t, loginWithGoogle } = useApp();
  const navigate = useNavigate();
  const [allPlayers, setAllPlayers] = useState([]);
  const [gate, setGate] = useState(null); // null | 'login' | 'sub' | 'role'
  const [query, setQuery] = useState("");
  const [position, setPosition] = useState("all");
  const [country, setCountry] = useState("all");
  const [foot, setFoot] = useState("all");
  const [trialOnly, setTrialOnly] = useState(false);

  useEffect(() => {
    api.get("/players")
      .then(({ data }) => { setAllPlayers(data || []); setGate(null); })
      .catch((err) => {
        const s = err?.response?.status;
        if (s === 401) setGate("login");
        else if (s === 402) setGate("sub");
        else if (s === 403) setGate("role");
      });
  }, []);

  const filtered = useMemo(() => {
    return allPlayers.filter((p) => {
      const q = query.toLowerCase();
      const matchQ = !q || p.name.toLowerCase().includes(q) || p.club.toLowerCase().includes(q);
      const matchPos = position === "all" || p.position === position;
      const matchCountry = country === "all" || p.country === country;
      const matchFoot = foot === "all" || p.foot === foot;
      const matchTrial = !trialOnly || p.openForTrial;
      return matchQ && matchPos && matchCountry && matchFoot && matchTrial;
    });
  }, [allPlayers, query, position, country, foot, trialOnly]);

  const clear = () => {
    setQuery("");
    setPosition("all");
    setCountry("all");
    setFoot("all");
    setTrialOnly(false);
  };

  const selectClass = "bg-[#0f1116] border-white/10 text-gray-200 h-10 focus:ring-blue-500";
  const contentClass = "bg-[#111318] border-white/10 text-gray-200";

  return (
    <div className="pt-16 min-h-screen">
      <div className="border-b border-white/5 bg-[#0a0c10]">
        <div className="max-w-6xl mx-auto px-5 py-12">
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white">{t.search.title}</h1>
          <p className="text-gray-400 mt-2">{t.search.subtitle}</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 py-8">
        {gate ? (
          <div className="bg-[#0f1116] border border-white/8 rounded-2xl py-20 px-6 text-center max-w-lg mx-auto">
            <div className="w-14 h-14 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mx-auto mb-5">
              <Lock className="w-6 h-6 text-blue-400" />
            </div>
            <h3 className="font-display text-xl font-bold text-white">{t.sub.needSub}</h3>
            {gate === "login" && (
              <Button onClick={loginWithGoogle} className="mt-6 bg-blue-600 hover:bg-blue-500 text-white h-11 px-7 rounded-xl">{t.nav.login}</Button>
            )}
            {gate === "sub" && (
              <Button onClick={() => navigate("/pricing")} className="mt-6 bg-blue-600 hover:bg-blue-500 text-white h-11 px-7 rounded-xl">{t.sub.viewPlans}</Button>
            )}
            {gate === "role" && (
              <Button onClick={() => navigate("/onboarding")} className="mt-6 bg-blue-600 hover:bg-blue-500 text-white h-11 px-7 rounded-xl">{t.onboard.continue}</Button>
            )}
          </div>
        ) : (
        <>
        {/* Filters */}
        <div className="bg-[#0f1116] border border-white/8 rounded-2xl p-5 mb-8">
          <div className="relative mb-4">
            <SearchIcon className="w-4 h-4 text-gray-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t.search.searchPlaceholder}
              className="pl-10 bg-[#0a0c10] border-white/10 text-gray-200 h-11 focus-visible:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs text-gray-500 mb-1.5 block">{t.search.position}</Label>
              <Select value={position} onValueChange={setPosition}>
                <SelectTrigger className={selectClass}><SelectValue /></SelectTrigger>
                <SelectContent className={contentClass}>
                  <SelectItem value="all">{t.search.all}</SelectItem>
                  {positions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-1.5 block">{t.search.country}</Label>
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger className={selectClass}><SelectValue /></SelectTrigger>
                <SelectContent className={contentClass}>
                  <SelectItem value="all">{t.search.all}</SelectItem>
                  {countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-1.5 block">{t.search.foot}</Label>
              <Select value={foot} onValueChange={setFoot}>
                <SelectTrigger className={selectClass}><SelectValue /></SelectTrigger>
                <SelectContent className={contentClass}>
                  <SelectItem value="all">{t.search.all}</SelectItem>
                  <SelectItem value="Left">Left</SelectItem>
                  <SelectItem value="Right">Right</SelectItem>
                  <SelectItem value="Both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <div className="flex items-center gap-2 h-10">
                <Switch id="trial" checked={trialOnly} onCheckedChange={setTrialOnly} className="data-[state=checked]:bg-emerald-500" />
                <Label htmlFor="trial" className="text-sm text-gray-300 cursor-pointer">{t.search.openTrial}</Label>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5">
            <span className="text-sm text-gray-500 flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4" />
              <strong className="text-white">{filtered.length}</strong> {t.search.results}
            </span>
            <Button onClick={clear} variant="ghost" className="text-gray-400 hover:text-white hover:bg-white/5 h-9">
              <X className="w-4 h-4 mr-1.5" /> {t.search.clear}
            </Button>
          </div>
        </div>

        {/* Results */}
        {filtered.length === 0 ? (
          <div className="text-center py-20 text-gray-500">{t.search.noResults}</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p) => <PlayerCard key={p.id} player={p} />)}
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
};

export default Search;
